/**
 * StudyHeader — the shared top strip of every study page: breadcrumb back to
 * course, mode icon + lesson title + mastery badge, progress N/M with bar.
 */
import { Link } from 'react-router-dom';
import { motion } from 'motion/react';
import { Icon, type IconName } from '@/components/Icon';
import { MasteryBadge } from '@/components/MasteryBadge';
import type { StudyMode } from '@/lib/types';

const MODE_ICON: Record<StudyMode, IconName> = {
  puzzle: 'puzzle',
  typing: 'typing',
  speaking: 'speaking',
};

interface Props {
  courseId: string;
  courseTitle: string;
  lessonTitle: string;
  sentenceId: string;
  mode: StudyMode;
  index: number;
  total: number;
  progressPct: number;
}

export function StudyHeader({
  courseId,
  courseTitle,
  lessonTitle,
  sentenceId,
  mode,
  index,
  total,
  progressPct,
}: Props): JSX.Element {
  return (
    <div className="flex items-start justify-between gap-4 flex-wrap">
      <div>
        <Link
          to={`/app/courses/${courseId}`}
          className="text-xs text-cream-400 hover:text-white font-arcade tracking-widest"
        >
          ← {courseTitle}
        </Link>
        <h1 className="mt-1 font-display text-2xl md:text-3xl font-bold text-white flex items-center gap-3 flex-wrap">
          <Icon name={MODE_ICON[mode]} size="xl" className="text-honey-400" />
          {lessonTitle}
          <MasteryBadge sentenceId={sentenceId} />
        </h1>
      </div>
      <div className="text-right text-xs text-cream-400">
        <div className="font-arcade text-honey-300">
          {String(index + 1).padStart(2, '0')} / {String(total).padStart(2, '0')}
        </div>
        <div className="mt-2 w-40 h-1 rounded-full bg-white/[0.06] overflow-hidden">
          <motion.div
            className="h-full bg-gradient-to-r from-honey-400 via-mint-400 to-ember-400"
            animate={{ width: `${progressPct}%` }}
            transition={{ duration: 0.4 }}
          />
        </div>
      </div>
    </div>
  );
}
